/**
 * Herkent of een productoptie een materiaal- of productkleur is (en dus
 * kleurstalen verdient), los van de taal waarin het label staat.
 *
 * De oude regex /\b(colou?r|kleur|farbe|couleur)\b/ miste samenstellingen
 * ("Materialfarbe", "lintkleur") en ids met een underscore ("color_material":
 * `_` telt als woordteken, dus \b matcht daar nooit). Daardoor kreeg het
 * satijnen lint in het Duits een kale dropdown zonder stalen.
 *
 * Bewust GEEN kleuroptie: drukmodi ("4/0 full colour", "printcolors",
 * "Drukkleur"), kleurgebruik/kleurmodus, aantal kleuren, lichtkleur
 * (kleurtemperatuur), kleurenwaaiers als product, veredeling/foliedruk en
 * de verborgen summary_image-hulpopties.
 */

export type ColorOptionLike = {
  id?: string | null;
  name?: string | null;
  label?: string | null;
  type?: string | null;
};

/** Kleine letters, ß -> ss, accenten weg, scheidingstekens -> spatie. */
export function normalizeColorText(input: unknown): string {
  return String(input ?? '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[®™]/g, ' ')
    .replace(/[_\-/.,:;|'’()[\]]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Kleurwoord als (deel van een) woord: colour/color, kleur, farbe/farbig,
// couleur, teinte, plus cs/da/es/it/pl/pt (barva, farve, colore, kolor) en
// "cor" alleen als los woord (anders matcht "corde", "decor").
const COLOR_WORD = /(colou?r|kleur|farbe|couleur|teinte|barva|farve|kolor)|\bcor\b/;

const NOT_A_MATERIAL_COLOR: RegExp[] = [
  /^summary image/,
  // Drukmodus: "print colours", "printcolors", "Drukkleur", "Druckfarbe",
  // "couleurs d'impression", "4/0 colour", "full colour", "cmyk".
  /(print(ing)?|druk|druck|impression|imprimee?s?) ?(colou?rs?|kleur(en)?|farben?|couleurs?)/,
  /(colou?rs?|kleur(en)?|farben?|couleurs?) ?(d ?)?(print(ing)?|druk|druck|impression)/,
  /\b[0-9] ?[0-9]? ?(colou?r|kleur|farbe|farbig|couleur)/,
  /\bfarbig\b/,
  /\bfull ?colou?r|\bfullcolou?r|vollfarb|farbigkeit|quadri|\bcmyk\b/,
  // Kleurgebruik, kleurmodus, kleurprofiel, kleurruimte, kleurbeheer.
  /(colou?r|kleur|farb|couleur) ?(gebruik|use|usage|mode|modus|profiel|profile|profil|space|ruimte|raum|management|beheer|correct)/,
  /(mode|modus|profil|espace) ?(de )?(colou?r|couleur|farb)/,
  // Aantal kleuren.
  /(aantal|number of|anzahl|nombre de|no of) ?(colou?rs?|kleuren|farben|couleurs)/,
  // Kleurtemperatuur / lichtkleur.
  /temperat|\blichtkleur|\blichtfarbe|light ?colou?r|couleur (de la )?lumiere|\bkelvin\b/,
  // Kleurenwaaier / stalenketting als product.
  /(colou?r|kleuren|farb|couleurs?) ?(guide|waaier|fan|facher|nuancier)|nuancier|color guides/,
  // Veredeling en foliedruk zijn afwerkingen, geen materiaalkleur.
  /\bfoil|folie(druk|pragung)|veredel|letterpress|blinddruk|dorure|kleurreferentie|colou?r reference|farbreferenz/,
];

/** Puur op id/naam/label: is dit een materiaal- of productkleur? */
export function isColorOptionKey(option: ColorOptionLike): boolean {
  if (option.type === 'color' || option.type === 'swatch') return true;
  const parts = [option.id, option.name, option.label].map(normalizeColorText).filter(Boolean);
  if (parts.length === 0) return false;
  // Een uitsluiting in een van de velden wint: "color_print" met label
  // "Drukmethode" is geen kleurkeuze.
  if (parts.some((part) => NOT_A_MATERIAL_COLOR.some((pattern) => pattern.test(part)))) return false;
  return parts.some((part) => COLOR_WORD.test(part));
}

/**
 * Detector plus waardencontrole: een optie met een kleurlabel maar waarden als
 * "4/0 Full colour" of "Overal geweven" krijgt geen stalen. Minstens de helft
 * van de waarden moet een herkenbare kleur (of "geen"/transparant) opleveren.
 */
export function shouldPresentAsColorOption(
  option: ColorOptionLike,
  values: unknown[],
  isResolved: (value: unknown) => boolean,
): boolean {
  if (!isColorOptionKey(option)) return false;
  if (option.type === 'color' || option.type === 'swatch') return true;
  if (!values.length) return false;
  const resolved = values.filter((value) => isResolved(value)).length;
  return resolved * 2 >= values.length;
}
